package com.sddamico.swigmore.util

import com.sddamico.swigmore.api.PollApi
import com.sddamico.swigmore.models.ComputerPollResponse
import com.sddamico.swigmore.models.CurrentPollResponse
import com.sddamico.swigmore.models.GameResultsResponse
import com.sddamico.swigmore.models.LeagueResponse
import com.sddamico.swigmore.models.MatchupsResponse
import com.sddamico.swigmore.models.PollArchiveResponse
import com.sddamico.swigmore.models.PollResultsResponse
import kotlinx.coroutines.CancellationException

/** The read-only data [rememberCachedResource] keeps across tabs, each with the fetch that loads it. */
sealed class CachedResource<T> {
  internal abstract suspend fun fetch(): T

  data object League : CachedResource<LeagueResponse>() {
    override suspend fun fetch() = PollApi.league()
  }

  data object Home : CachedResource<HomeData>() {
    override suspend fun fetch(): HomeData {
      val current = PollApi.current()
      return HomeData(
        current = current,
        results = if (current.hasPublishedPoll) PollApi.results() else null,
        slate = orNull { PollApi.matchups() },
      )
    }
  }

  data object Results : CachedResource<GameResultsResponse>() {
    override suspend fun fetch() = PollApi.gameResults()
  }

  data object ComputerPoll : CachedResource<ComputerPollResponse>() {
    override suspend fun fetch() = PollApi.computerPoll()
  }

  data class Archive(val season: Int?) : CachedResource<PollArchiveResponse>() {
    override suspend fun fetch() = PollApi.archive(season)
  }
}

/**
 * What home needs besides the league. [results] is null before the first poll publishes; [slate] is
 * null when the Fantrax data can't load, which blanks only its section, as a failed league does.
 */
class HomeData(
  val current: CurrentPollResponse,
  val results: PollResultsResponse?,
  val slate: MatchupsResponse?,
)

private suspend fun <T> orNull(fetch: suspend () -> T): T? = try {
  fetch()
} catch (e: CancellationException) {
  throw e
} catch (e: Exception) {
  null
}
